import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { listProducts } from '../lib/api';
import { formatMoney } from '../lib/pricing';

// -------- Config per category ------------------------------------------------

const CATEGORY_META = {
  'weight-loss': {
    key: 'weightLoss',
    label: 'Weight Loss',
    treatmentsRoute: '/weight-loss/treatments',
    quizRoute: '/weight-loss/quiz',
    summaryFor: (a) => ({
      goals: labelize(a.goals, {
        lose_weight: 'Lose weight',
        reduce_cravings: 'Reduce cravings',
        more_energy: 'More energy',
        build_habits: 'Build sustainable habits',
        health_metrics: 'Improve blood sugar / cholesterol',
        confidence: 'Feel more confident',
      }),
      prefs: [
        a.preferred_form && `Prefers ${{
          injection: 'weekly injections',
          sublingual: 'sublingual (no needles)',
          either: 'either form',
          provider: "provider's recommendation",
        }[a.preferred_form] || a.preferred_form}`,
        a.prior_glp1 && ({
          current: 'Currently on a GLP-1',
          previous: 'Previously used a GLP-1',
          never: 'New to GLP-1 medications',
          not_sure: 'Unsure about prior GLP-1 use',
        }[a.prior_glp1]),
        a.triedBefore && ({
          none: 'First formal weight-loss program',
          diet_exercise: 'Has tried diet & exercise',
          prescription: 'Has tried prescription medication',
          multiple: 'Multiple prior programs',
        }[a.triedBefore]),
      ].filter(Boolean),
    }),
    // Ranker returns [product, ...] ordered best-first.
    rank: (products, a) => {
      const wantsInject = a.preferred_form === 'injection';
      const wantsSub    = a.preferred_form === 'sublingual';
      const isNewGlp1   = a.prior_glp1 === 'never' || a.prior_glp1 === 'not_sure';
      const score = (p) => {
        let s = 0;
        // Preferred form
        if (wantsInject && p.form === 'Injection') s += 4;
        if (wantsSub && p.form && p.form.startsWith('Oral')) s += 4;
        // New users → prefer microdose
        if (isNewGlp1 && /microdose/i.test(p.slug)) s += 3;
        // Featured
        if (p.featured) s += 1;
        // Cheaper products score slightly higher as fallback
        s += Math.max(0, 4 - Math.floor((p.monthly_price || 500) / 100));
        return s;
      };
      return [...products].sort((a2, b2) => score(b2) - score(a2));
    },
  },
};

function labelize(arr, dict) {
  if (!Array.isArray(arr)) return [];
  return arr.map((v) => dict[v] || v).filter(Boolean);
}

// -------- Component ---------------------------------------------------------

export default function Results({ categorySlug }) {
  const meta = CATEGORY_META[categorySlug];
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [answers, setAnswers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interest, setInterest] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Load quiz answers (may be null if user landed directly)
        try {
          const raw = localStorage.getItem(`alfa_quiz_${categorySlug}`);
          if (raw) setAnswers(JSON.parse(raw).answers || {});
        } catch { /* ignore */ }
        // Product interest carried in from a specific card
        try {
          const iRaw = localStorage.getItem(`alfa_interest_${categorySlug}`);
          if (iRaw) setInterest(iRaw);
        } catch { /* ignore */ }
        const prods = await listProducts(meta.key);
        if (!cancelled) setProducts(prods.filter((p) => p.status === 'active'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [categorySlug, meta.key]);

  const summary = useMemo(() => meta.summaryFor(answers || {}), [meta, answers]);
  const ranked = useMemo(() => {
    let list = meta.rank(products, answers || {});
    if (interest) {
      const idx = list.findIndex((p) => p.slug === interest);
      if (idx > 0) {
        const [p] = list.splice(idx, 1);
        list = [p, ...list];
      }
    }
    return list;
  }, [meta, products, answers, interest]);

  // Pick the top three for Best/Alt/Lower-Cost. Lower-cost = cheapest of remaining.
  const featured = useMemo(() => {
    if (ranked.length === 0) return [];
    const top = ranked[0];
    const alt = ranked[1] || null;
    // Lower-cost: cheapest active option that's not top or alt
    const rest = ranked.filter((p) => p.slug !== top.slug && (!alt || p.slug !== alt.slug));
    const lower = rest.sort((a, b) => (a.monthly_price || 999) - (b.monthly_price || 999))[0] || null;
    return [
      top && { p: top, label: 'Best Match' },
      alt && { p: alt, label: 'Alternative Option' },
      lower && { p: lower, label: 'Lower-Cost Option' },
    ].filter(Boolean);
  }, [ranked]);

  const firstName = answers?.contact?.name?.split(' ')[0] || null;

  return (
    <div className="min-h-screen relative flex flex-col">
      <div className="telegra-bg" aria-hidden="true" />
      <Header />

      <main className="flex-1 px-4 sm:px-6 pt-[96px] sm:pt-[128px] pb-16 sm:pb-24 max-w-[1200px] mx-auto w-full">
        <span className="inline-flex items-center gap-2 text-emerald-700 text-[13px] uppercase tracking-[0.22em] font-semibold bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5">
          <CheckCircle2 size={14} /> Assessment complete
        </span>
        <h1 data-testid="results-title" className="mt-5 font-display uppercase text-[36px] sm:text-[54px] md:text-[66px] leading-[0.98] tracking-[-0.015em] text-[color:var(--alfa-navy)]">
          {firstName ? <>{firstName}, <span className="text-[color:var(--alfa-royal)]">here&apos;s what we learned.</span></> : <>Your personalized {meta.label.toLowerCase()} options.</>}
        </h1>
        <p className="mt-5 text-[16.5px] text-neutral-950 leading-relaxed max-w-2xl">
          These options are based on your stated goals and preferences. A licensed medical provider will review your health information and determine whether treatment is appropriate.
        </p>

        {/* Summary chips */}
        {(summary.goals.length > 0 || summary.prefs.length > 0) && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {summary.goals.length > 0 && (
              <SummaryCard title="Your primary goals" items={summary.goals} />
            )}
            {summary.prefs.length > 0 && (
              <SummaryCard title="Your preferences" items={summary.prefs} />
            )}
          </div>
        )}

        {/* Featured ranked cards */}
        {loading && <p className="mt-10 text-neutral-950">Curating your options…</p>}

        {!loading && featured.length > 0 && (
          <section className="mt-12">
            <h2 className="font-display uppercase text-[22px] sm:text-[28px] tracking-[-0.01em] text-[color:var(--alfa-navy)]">
              Treatments to discuss with your provider
            </h2>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {featured.map(({ p, label }) => (
                <RankedCard key={p.slug} p={p} label={label} onSelect={() => navigate(`/checkout/${p.slug}`)} />
              ))}
            </div>
            <p className="mt-6 text-[13px] text-neutral-950 max-w-3xl leading-relaxed flex items-start gap-2">
              <ShieldCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
              Automated match labels are based on your stated preferences — not a diagnosis or approval. A licensed provider makes the final treatment and dosage decision.
            </p>
          </section>
        )}

        <div className="mt-12 flex flex-col sm:flex-row gap-3">
          <Link
            to={meta.treatmentsRoute}
            data-testid="results-see-all"
            className="inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white uppercase text-[14px] tracking-[0.14em] font-semibold px-5 py-3.5 rounded-sm"
          >
            See all {meta.label.toLowerCase()} treatments <ArrowRight size={15} />
          </Link>
          <Link
            to={meta.quizRoute}
            className="inline-flex items-center justify-center gap-2 bg-white/70 hover:bg-white text-neutral-900 uppercase text-[14px] tracking-[0.14em] font-semibold px-5 py-3.5 rounded-sm border border-neutral-300"
          >
            Retake assessment
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function SummaryCard({ title, items }) {
  return (
    <div className="rounded-md border border-neutral-900/10 bg-white/85 backdrop-blur p-5">
      <p className="uppercase text-[11.5px] tracking-[0.2em] text-neutral-950">{title}</p>
      <ul className="mt-3 space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-[15px] text-neutral-900 flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-neutral-900 shrink-0" />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RankedCard({ p, label, onSelect }) {
  const badgeStyle = {
    'Best Match':         'bg-neutral-900 text-white',
    'Alternative Option': 'bg-white text-neutral-900 border border-neutral-900',
    'Lower-Cost Option':  'bg-emerald-600 text-white',
  }[label] || 'bg-white text-neutral-900 border border-neutral-300';
  return (
    <article data-testid={`ranked-${p.slug}`} className="rounded-md overflow-hidden bg-white/85 backdrop-blur border border-neutral-900/10 shadow-sm hover:shadow-lg transition-shadow flex flex-col">
      <div className="relative aspect-[16/10] bg-neutral-100">
        {p.img ? (
          <img src={p.img} alt={p.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-400 text-[13px] uppercase tracking-[0.2em]">
            {p.form}
          </div>
        )}
        <span className={`absolute top-3 left-3 text-[11px] uppercase tracking-[0.16em] font-semibold px-2.5 py-1 rounded-sm ${badgeStyle}`}>
          <span className="inline-flex items-center gap-1"><Sparkles size={11} /> {label}</span>
        </span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display uppercase text-[18px] leading-tight text-[color:var(--alfa-navy)]">{p.name}</h3>
          <div className="text-right shrink-0">
            <span className="font-display text-[22px] text-neutral-950">{formatMoney(p.monthly_price)}</span>
            <span className="text-[13px] text-neutral-950">/mo</span>
          </div>
        </div>
        <p className="mt-1 text-[12px] uppercase tracking-[0.16em] text-neutral-950">{p.form}</p>
        <p className="mt-3 text-[14px] text-neutral-950 leading-relaxed">{p.desc}</p>
        <div className="mt-auto pt-5">
          <button
            data-testid={`select-ranked-${p.slug}`}
            onClick={onSelect}
            className="btn-primary arrow-suffix w-full inline-flex items-center justify-center gap-2 uppercase text-[13px] tracking-[0.14em] font-semibold px-4 py-3 rounded-sm bg-neutral-900 hover:bg-neutral-800 text-white"
          >
            Select this treatment <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </article>
  );
}
